/**
 * Referral payload — one recommendation of a service provider (spec sections
 * 10, 13, 14, 17).
 *
 * What this captures is a name and enough context for a human being to find
 * that person: what they do, roughly where they work, and whatever contact
 * detail the referrer happens to know. It is an **outreach queue entry**, not a
 * contact record. Nothing is ever sent to the person named (spec section 17),
 * so the fields are not validated as though a machine were about to mail them.
 *
 * Two consequences of that, both deliberate:
 *
 * 1. **`websiteOrContact` is free text.** "hallbrookfarm.com", "410-555-0134",
 *    and "ask at the barn, she is there Tuesdays" are all useful answers to
 *    "how would we find them". Validating it as a URL would reject two of the
 *    three. It is trimmed and length-capped, and that is all.
 * 2. **Only the name is required.** A referrer who knows a farrier's name and
 *    nothing else still knows something worth having. Every extra required
 *    field costs referrals and buys nothing that manual outreach cannot ask for
 *    on the phone.
 *
 * `signupRef` is the opaque token the signup response issued, passed in the
 * body rather than in the URL (spec section 19). It is **never** a validation
 * failure: anything unusable is dropped to `undefined` here and the referral is
 * accepted unattributed. A referral that is rejected because a token expired
 * while someone was typing is a lost referral, and the token's only job is to
 * attach the row to a lead that the server resolves for itself.
 */

import { z } from 'zod';
import { honeypotSchema, HONEYPOT_FIELD, optionalText, requiredText } from './fields';

/** Field caps. Also the `maxLength` the form's controls are rendered with. */
export const REFERRAL_LIMITS = {
  recommendedName: { min: 2, max: 160 },
  categoryText: 80,
  locationText: 120,
  websiteOrContact: 200,
  note: 500,
} as const;

/**
 * Generous ceiling on the opaque token. Long enough for any token this app
 * issues (see services/signupRef.ts), short enough that a padded body cannot
 * be pushed through this field.
 */
export const SIGNUP_REF_MAX_LENGTH = 512;

/**
 * Accepts a token-shaped string and silently drops everything else. There is no
 * error branch on purpose — see the file header, and rule 3 of spec section 17:
 * the response must never depend on whether a token, or the lead behind it,
 * turned out to be real.
 */
export const referralSignupRefSchema = z.preprocess((value) => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (trimmed === '' || trimmed.length > SIGNUP_REF_MAX_LENGTH) return undefined;
  return trimmed;
}, z.string().optional());

/**
 * Which thank-you page the referral came from. The server treats this as a
 * hint: when the signup token resolves to a real lead, that lead's audience
 * wins (routes/referrals.ts).
 */
export const referrerTypeSchema = z.enum(['owner', 'provider'], {
  error: 'That recommendation could not be read. Reload the page and try again.',
});

export const referralSchema = z.object({
  recommendedName: requiredText(
    REFERRAL_LIMITS.recommendedName.min,
    REFERRAL_LIMITS.recommendedName.max,
    'Their name or business name',
  ),
  categoryText: optionalText(
    REFERRAL_LIMITS.categoryText,
    `Keep what they do under ${REFERRAL_LIMITS.categoryText} characters.`,
  ),
  locationText: optionalText(
    REFERRAL_LIMITS.locationText,
    `Keep the location under ${REFERRAL_LIMITS.locationText} characters.`,
  ),
  websiteOrContact: optionalText(
    REFERRAL_LIMITS.websiteOrContact,
    `Keep the contact detail under ${REFERRAL_LIMITS.websiteOrContact} characters.`,
  ),
  note: optionalText(
    REFERRAL_LIMITS.note,
    `Keep the note under ${REFERRAL_LIMITS.note} characters.`,
  ),
  referrerType: referrerTypeSchema,
  [HONEYPOT_FIELD]: honeypotSchema,
  signupRef: referralSignupRefSchema,
});

export type ReferralInput = z.infer<typeof referralSchema>;
/** The pre-normalization shape, i.e. what the form sends. */
export type ReferralSubmission = z.input<typeof referralSchema>;
export type ReferrerType = z.infer<typeof referrerTypeSchema>;
